import {
  useCallback,
  useEffect,
  useId,
  useState,
  type FormEvent,
  type ReactNode,
} from 'react';
import type { Profile, Role } from '@ambec/shared';
import { ApiError, apiFetch } from '../../lib/api';
import { useAuth } from '../../auth/AuthContext';
import { iniciales } from '../../lib/iniciales';
import { Modal } from '../../components/Modal';
import { Spinner } from '../../components/Spinner';

const ROLES: { value: Role; label: string }[] = [
  { value: 'user', label: 'Usuario' },
  { value: 'admin', label: 'Administrador' },
];

const MENSAJES_ERROR: Record<string, string> = {
  cannot_delete_self: 'No puedes eliminar tu propia cuenta.',
};

function traducirError(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.code && MENSAJES_ERROR[err.code]) return MENSAJES_ERROR[err.code];
    return err.message;
  }
  return 'Ocurrió un error inesperado. Inténtalo de nuevo.';
}

function ChipRol({ role }: { role: Role }) {
  const esAdmin = role === 'admin';
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium ring-1 ring-inset ${
        esAdmin
          ? 'bg-azul-profundo/10 text-azul-profundo ring-azul-profundo/20'
          : 'bg-slate-100 text-slate-700 ring-slate-300'
      }`}
    >
      {esAdmin ? 'Administrador' : 'Usuario'}
    </span>
  );
}

function ChipEstado({ activo }: { activo: boolean }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-medium ring-1 ring-inset ${
        activo
          ? 'bg-emerald-50 text-emerald-700 ring-emerald-300'
          : 'bg-slate-100 text-slate-600 ring-slate-300'
      }`}
    >
      <span
        aria-hidden="true"
        className={`h-1.5 w-1.5 rounded-full ${activo ? 'bg-emerald-500' : 'bg-slate-400'}`}
      />
      {activo ? 'Activo' : 'Inactivo'}
    </span>
  );
}

// ─────────────────────────────── Formulario ────────────────────────────────

interface FormProps {
  usuario: Profile | null; // null = creación
  onCerrar: () => void;
  onGuardado: () => void;
}

function UsuarioForm({ usuario, onCerrar, onGuardado }: FormProps) {
  const esEdicion = usuario !== null;
  const [email, setEmail] = useState(usuario?.email ?? '');
  const [fullName, setFullName] = useState(usuario?.full_name ?? '');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<Role>(usuario?.role ?? 'user');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const emailId = useId();
  const nombreId = useId();
  const passId = useId();
  const rolId = useId();

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (enviando) return;
    setError(null);
    setEnviando(true);
    try {
      if (esEdicion) {
        const body: Record<string, unknown> = { full_name: fullName.trim(), role };
        if (password.trim()) body.password = password;
        await apiFetch(`/api/admin/users/${usuario.id}`, {
          method: 'PATCH',
          body: JSON.stringify(body),
        });
      } else {
        await apiFetch('/api/admin/users', {
          method: 'POST',
          body: JSON.stringify({
            email: email.trim(),
            password,
            full_name: fullName.trim() || undefined,
            role,
          }),
        });
      }
      onGuardado();
    } catch (err) {
      setError(traducirError(err));
      setEnviando(false);
    }
  }

  const inputClass = 'campo';
  const labelClass = 'etiqueta';

  return (
    <form onSubmit={onSubmit} noValidate className="flex flex-col gap-5">
      {error && (
        <div role="alert" className="alerta-error">
          {error}
        </div>
      )}

      <div>
        <label htmlFor={emailId} className={labelClass}>
          Correo electrónico
        </label>
        <input
          id={emailId}
          type="email"
          required
          value={email}
          disabled={esEdicion}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="nombre@empresa.cl"
          className={inputClass}
        />
        {esEdicion && (
          <p className="mt-1.5 text-xs font-light text-gris">
            El correo no puede modificarse.
          </p>
        )}
      </div>

      <div>
        <label htmlFor={nombreId} className={labelClass}>
          Nombre completo
        </label>
        <input
          id={nombreId}
          type="text"
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          placeholder="Nombre y apellido"
          className={inputClass}
        />
      </div>

      <div>
        <label htmlFor={passId} className={labelClass}>
          {esEdicion ? 'Nueva contraseña (opcional)' : 'Contraseña'}
        </label>
        <input
          id={passId}
          type="password"
          required={!esEdicion}
          value={password}
          autoComplete="new-password"
          onChange={(e) => setPassword(e.target.value)}
          placeholder={esEdicion ? 'Dejar en blanco para no cambiarla' : 'Mínimo 8 caracteres'}
          className={inputClass}
        />
      </div>

      <div>
        <label htmlFor={rolId} className={labelClass}>
          Rol
        </label>
        <select
          id={rolId}
          value={role}
          onChange={(e) => setRole(e.target.value as Role)}
          className={inputClass}
        >
          {ROLES.map((r) => (
            <option key={r.value} value={r.value}>
              {r.label}
            </option>
          ))}
        </select>
      </div>

      <div className="mt-2 flex justify-end gap-3">
        <button type="button" onClick={onCerrar} className="btn-fantasma">
          Cancelar
        </button>
        <button type="submit" disabled={enviando} className="btn-primario">
          {enviando ? 'Guardando…' : esEdicion ? 'Guardar cambios' : 'Crear usuario'}
        </button>
      </div>
    </form>
  );
}

// ─────────────────────── Confirmación de eliminación ───────────────────────

function ConfirmarEliminar({
  usuario,
  onCerrar,
  onEliminado,
}: {
  usuario: Profile;
  onCerrar: () => void;
  onEliminado: () => void;
}) {
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onEliminar() {
    setError(null);
    setEnviando(true);
    try {
      await apiFetch(`/api/admin/users/${usuario.id}`, { method: 'DELETE' });
      onEliminado();
    } catch (err) {
      setError(traducirError(err));
      setEnviando(false);
    }
  }

  return (
    <Modal titulo="Eliminar usuario" onCerrar={onCerrar}>
      {error && (
        <div role="alert" className="alerta-error mb-5">
          {error}
        </div>
      )}
      <p className="text-sm font-light leading-relaxed text-texto">
        ¿Seguro que quieres eliminar a{' '}
        <span className="font-medium text-azul-profundo">
          {usuario.full_name || usuario.email}
        </span>
        ? Esta acción no se puede deshacer.
      </p>
      <div className="mt-6 flex justify-end gap-3">
        <button type="button" onClick={onCerrar} className="btn-fantasma">
          Cancelar
        </button>
        <button type="button" onClick={onEliminar} disabled={enviando} className="btn-peligro">
          {enviando ? 'Eliminando…' : 'Eliminar'}
        </button>
      </div>
    </Modal>
  );
}

// ──────────────────────────────── Página ───────────────────────────────────

type Dialogo =
  | { tipo: 'crear' }
  | { tipo: 'editar'; usuario: Profile }
  | { tipo: 'eliminar'; usuario: Profile }
  | null;

type Filtro = 'todas' | 'activas' | 'inactivas';

const FILTROS: { id: Filtro; label: string }[] = [
  { id: 'todas', label: 'Todas' },
  { id: 'activas', label: 'Activas' },
  { id: 'inactivas', label: 'Inactivas' },
];

/** Tile de indicador (icono en contenedor pastel + número bold + label). */
function StatTile({ icono, valor, label }: { icono: ReactNode; valor: number; label: string }) {
  return (
    <div className="tarjeta p-4">
      <span
        aria-hidden="true"
        className="flex h-9 w-9 items-center justify-center rounded-lg bg-celeste/40 text-azul-profundo"
      >
        {icono}
      </span>
      <p className="mt-3 text-xl font-medium leading-none tabular-nums text-azul-profundo">{valor}</p>
      <p className="mt-1 text-xs font-normal text-gris">{label}</p>
    </div>
  );
}

const statIcon = 'h-5 w-5';

export default function Usuarios() {
  const { profile: yo } = useAuth();
  const [usuarios, setUsuarios] = useState<Profile[]>([]);
  const [cargando, setCargando] = useState(true);
  const [errorCarga, setErrorCarga] = useState<string | null>(null);
  const [dialogo, setDialogo] = useState<Dialogo>(null);
  /** Id del usuario cuyo estado activo se está alternando (para deshabilitar). */
  const [alternando, setAlternando] = useState<string | null>(null);
  const [errorAccion, setErrorAccion] = useState<string | null>(null);
  const [filtro, setFiltro] = useState<Filtro>('todas');

  const activas = usuarios.filter((u) => u.is_active).length;
  const admins = usuarios.filter((u) => u.role === 'admin').length;
  const visibles = usuarios.filter((u) =>
    filtro === 'todas' ? true : filtro === 'activas' ? u.is_active : !u.is_active,
  );

  const cargar = useCallback(async () => {
    setCargando(true);
    setErrorCarga(null);
    try {
      const data = await apiFetch<{ users: Profile[] }>('/api/admin/users');
      setUsuarios(data.users);
    } catch (err) {
      setErrorCarga(traducirError(err));
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  async function alternarActivo(u: Profile) {
    setErrorAccion(null);
    setAlternando(u.id);
    try {
      await apiFetch(`/api/admin/users/${u.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ is_active: !u.is_active }),
      });
      await cargar();
    } catch (err) {
      setErrorAccion(traducirError(err));
    } finally {
      setAlternando(null);
    }
  }

  function cerrarYRefrescar() {
    setDialogo(null);
    void cargar();
  }

  return (
    <div className="flex flex-col gap-5">
      {/* Encabezado de página (patrón de la guía visual) */}
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="titulo-pagina">Usuarios</h1>
          <p className="subtitulo-pagina">Administra las cuentas con acceso a la plataforma.</p>
        </div>
        <button
          type="button"
          onClick={() => setDialogo({ tipo: 'crear' })}
          className="btn-primario"
        >
          <svg
            viewBox="0 0 24 24"
            className="h-4 w-4"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            aria-hidden="true"
          >
            <path d="M12 5v14M5 12h14" />
          </svg>
          Nuevo usuario
        </button>
      </header>

      {/* Indicadores */}
      {!cargando && !errorCarga && (
        <div className="grid gap-3 sm:grid-cols-3">
          <StatTile
            valor={usuarios.length}
            label="Cuentas totales"
            icono={
              <svg
                viewBox="0 0 24 24"
                className={statIcon}
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
            }
          />
          <StatTile
            valor={activas}
            label="Cuentas activas"
            icono={
              <svg
                viewBox="0 0 24 24"
                className={statIcon}
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <path d="m9 11 3 3L22 4" />
              </svg>
            }
          />
          <StatTile
            valor={admins}
            label="Administradores"
            icono={
              <svg
                viewBox="0 0 24 24"
                className={statIcon}
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" />
              </svg>
            }
          />
        </div>
      )}

      {errorAccion && (
        <div role="alert" className="alerta-error">
          {errorAccion}
        </div>
      )}

      <div className="tarjeta overflow-hidden">
        {/* Header de la card: título + contexto + filtros pill */}
        {!cargando && !errorCarga && usuarios.length > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-3 px-5 pb-1 pt-4">
            <p className="text-sm font-medium text-azul-noche">
              Cuentas{' '}
              <span className="font-normal text-gris">( {visibles.length} )</span>
            </p>
            <div role="group" aria-label="Filtrar por estado" className="flex gap-2">
              {FILTROS.map((f) => (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => setFiltro(f.id)}
                  aria-pressed={filtro === f.id}
                  className={`pill ${filtro === f.id ? 'pill-activa' : ''}`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>
        )}
        {cargando ? (
          <div
            role="status"
            aria-live="polite"
            className="flex items-center justify-center gap-3 px-6 py-16 text-sm font-normal text-gris"
          >
            <Spinner tamano="sm" />
            Cargando usuarios…
          </div>
        ) : errorCarga ? (
          <div className="px-6 py-16 text-center">
            <p className="text-sm text-red-700">{errorCarga}</p>
            <button
              type="button"
              onClick={() => void cargar()}
              className="btn-secundario mt-4"
            >
              Reintentar
            </button>
          </div>
        ) : usuarios.length === 0 ? (
          <div className="px-6 py-16 text-center text-sm font-light text-gris">
            No hay usuarios registrados todavía.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] border-collapse text-left">
              <thead>
                <tr className="border-b border-slate-300 bg-[#F8FAFC]">
                  <th scope="col" className="th px-5">
                    Usuario
                  </th>
                  <th scope="col" className="th px-5">
                    Rol
                  </th>
                  <th scope="col" className="th px-5">
                    Estado
                  </th>
                  <th scope="col" className="th px-5 text-right">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {visibles.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-5 py-8 text-center text-sm font-normal text-gris">
                      No hay cuentas {filtro === 'activas' ? 'activas' : 'inactivas'}.
                    </td>
                  </tr>
                )}
                {visibles.map((u) => {
                  const esYo = u.id === yo?.id;
                  return (
                    <tr key={u.id} className="text-sm transition-colors duration-150 hover:bg-celeste/[0.08]">
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-3">
                          <span
                            aria-hidden="true"
                            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-celeste/40 text-xs font-semibold text-azul-profundo"
                          >
                            {iniciales(u.full_name, u.email)}
                          </span>
                          <div className="flex min-w-0 flex-col">
                            <span className="font-medium text-azul-noche">
                              {u.full_name || '—'}
                              {esYo && (
                                <span className="ml-2 text-[11px] font-normal text-gris">
                                  (tú)
                                </span>
                              )}
                            </span>
                            <span className="text-xs font-normal text-gris">{u.email}</span>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-3">
                        <ChipRol role={u.role} />
                      </td>
                      <td className="px-5 py-3">
                        <ChipEstado activo={u.is_active} />
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            type="button"
                            onClick={() => setDialogo({ tipo: 'editar', usuario: u })}
                            className="inline-flex h-7 items-center rounded-full border border-azul-profundo/30 px-3 text-[13px] font-medium text-azul-profundo transition-colors duration-150 ease-out hover:bg-azul-profundo/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-azul-profundo"
                          >
                            Editar
                          </button>
                          <button
                            type="button"
                            onClick={() => void alternarActivo(u)}
                            disabled={alternando === u.id}
                            className="btn-tabla text-gris hover:bg-slate-100 hover:text-azul-noche"
                          >
                            {alternando === u.id
                              ? '…'
                              : u.is_active
                                ? 'Desactivar'
                                : 'Activar'}
                          </button>
                          <button
                            type="button"
                            onClick={() => setDialogo({ tipo: 'eliminar', usuario: u })}
                            disabled={esYo}
                            title={esYo ? 'No puedes eliminar tu propia cuenta.' : undefined}
                            className="btn-tabla text-red-600 hover:bg-red-50 focus-visible:!outline-red-600"
                          >
                            Eliminar
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {(dialogo?.tipo === 'crear' || dialogo?.tipo === 'editar') && (
        <Modal
          titulo={dialogo.tipo === 'crear' ? 'Nuevo usuario' : 'Editar usuario'}
          onCerrar={() => setDialogo(null)}
        >
          <UsuarioForm
            usuario={dialogo.tipo === 'editar' ? dialogo.usuario : null}
            onCerrar={() => setDialogo(null)}
            onGuardado={cerrarYRefrescar}
          />
        </Modal>
      )}

      {dialogo?.tipo === 'eliminar' && (
        <ConfirmarEliminar
          usuario={dialogo.usuario}
          onCerrar={() => setDialogo(null)}
          onEliminado={cerrarYRefrescar}
        />
      )}
    </div>
  );
}
